import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getSystemRevenueConfig,
  setMonthlyFreeCoinRate,
  setPlatformFeeRate,
} from "@/services/firebase/revenue";
import { getDramas, updateDrama } from "@/services/firebase/drama";
import { getCompanies } from "@/services/firebase/company";
import { Megaphone, Pencil, X, Search, Smartphone } from "lucide-react";
import type { IapPlatform } from "@/types/revenue";
import { AdminPage } from "@/components/admin-ui/admin-page";
import { AdminPageHeader } from "@/components/admin-ui/admin-page-header";
import { AdminDataTable } from "@/components/admin-ui/admin-data-table";
import type { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { AdminLoadingSkeleton } from "@/components/admin-ui/admin-loading-skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

function getCurrentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

interface DramaRateRow {
  drama_doc_id: string;
  drama_id: string;
  drama_title: string;
  company_doc_id?: string;
  company_name: string;
  rate: number | null;
}

const tableColumns: AdminTableColumn[] = [
  { key: "drama_title", label: "劇集", sortable: true },
  { key: "company_name", label: "片商", sortable: true },
  { key: "rate", label: "Paid coin 費率 (NTD/coin)", sortable: true },
  { key: "actions", label: "操作" },
];

export default function RevenueConfigPage() {
  const queryClient = useQueryClient();

  const { data: dramas = [], isLoading: loadingDramas } = useQuery({
    queryKey: ["dramas"],
    queryFn: getDramas,
  });

  const { data: companies = [], isLoading: loadingCompanies } = useQuery({
    queryKey: ["companies"],
    queryFn: getCompanies,
  });

  const { data: sysConfig, isLoading: loadingSys } = useQuery({
    queryKey: ["system-revenue-config"],
    queryFn: getSystemRevenueConfig,
  });

  const [editForm, setEditForm] = useState<{
    open: boolean;
    drama: DramaRateRow | null;
    value: string;
  }>({ open: false, drama: null, value: "" });

  const [searchText, setSearchText] = useState("");
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const [onlyUnset, setOnlyUnset] = useState(false);

  // Free rate state
  const [freeRatePeriod, setFreeRatePeriod] = useState(getCurrentPeriod());
  const [freeRateValue, setFreeRateValue] = useState<string>("0");
  const [freeRateNote, setFreeRateNote] = useState("");

  React.useEffect(() => {
    const existing = sysConfig?.monthly_free_coin_rates?.[freeRatePeriod];
    if (existing) {
      setFreeRateValue(String(existing.rate));
      setFreeRateNote(existing.note || "");
    } else {
      setFreeRateValue("0");
      setFreeRateNote("");
    }
  }, [sysConfig, freeRatePeriod]);

  // Platform fee state
  const [feePlatform, setFeePlatform] = useState<IapPlatform>("ios");
  const [feePeriod, setFeePeriod] = useState(getCurrentPeriod());
  const [feeValue, setFeeValue] = useState<string>("0.15");
  const [feeNote, setFeeNote] = useState("");

  React.useEffect(() => {
    const existing =
      sysConfig?.platform_fees?.[feePlatform]?.[feePeriod];
    if (existing) {
      setFeeValue(String(existing.rate));
      setFeeNote(existing.note || "");
    } else {
      // 若該月沒公告，帶入最近一筆歷史值（若有）
      const entries = sysConfig?.platform_fees?.[feePlatform] ?? {};
      const latest = Object.keys(entries)
        .filter((k) => /^\d{4}-\d{2}$/.test(k) && k <= feePeriod)
        .sort()
        .pop();
      if (latest) {
        setFeeValue(String(entries[latest].rate));
      } else {
        setFeeValue("0.15");
      }
      setFeeNote("");
    }
  }, [sysConfig, feePlatform, feePeriod]);

  // Build rows: one per drama, reading paid_coin_rate directly from drama doc
  const rows: DramaRateRow[] = useMemo(() => {
    const byDocId = new Map<string, string>();
    for (const c of companies) {
      byDocId.set(c.id, c.company_name || "");
    }

    return dramas.map((d) => {
      let companyName = "未指定";
      if (d.company) {
        companyName = byDocId.get(d.company) || `(找不到片商 ${d.company})`;
      }
      const rawRate = d.paid_coin_rate;
      const hasRate = typeof rawRate === "number" && rawRate >= 0;
      return {
        drama_doc_id: d.id || d.drama_id,
        drama_id: d.drama_id,
        drama_title: d.drama_title || "(未命名)",
        company_doc_id: d.company,
        company_name: companyName,
        rate: hasRate ? (rawRate as number) : null,
      };
    });
  }, [dramas, companies]);

  const companyOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) {
      if (r.company_doc_id) map.set(r.company_doc_id, r.company_name);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return rows.filter((r) => {
      if (companyFilter && r.company_doc_id !== companyFilter) return false;
      if (onlyUnset && r.rate != null) return false;
      if (q) {
        return (
          r.drama_title.toLowerCase().includes(q) ||
          r.drama_id.toLowerCase().includes(q) ||
          r.company_name.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [rows, searchText, companyFilter, onlyUnset]);

  const setCount = rows.filter((r) => r.rate != null).length;
  const unsetCount = rows.length - setCount;

  const saveRateMutation = useMutation({
    mutationFn: async () => {
      if (!editForm.drama) throw new Error("No drama selected");
      const value = parseFloat(editForm.value);
      if (!Number.isFinite(value) || value < 0) {
        throw new Error("費率必須為 ≥ 0 的數字");
      }
      await updateDrama(editForm.drama.drama_doc_id, {
        paid_coin_rate: value,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dramas"] });
      setEditForm({ open: false, drama: null, value: "" });
    },
    onError: (err: Error) => alert(`儲存失敗：${err.message}`),
  });

  const clearRateMutation = useMutation({
    mutationFn: async (docId: string) =>
      updateDrama(docId, { paid_coin_rate: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dramas"] });
    },
    onError: (err: Error) => alert(`清除失敗：${err.message}`),
  });

  const announceFreeRateMutation = useMutation({
    mutationFn: () =>
      setMonthlyFreeCoinRate(
        freeRatePeriod,
        parseFloat(freeRateValue) || 0,
        freeRateNote
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-revenue-config"] });
      alert(
        `✅ ${freeRatePeriod} 的 Free coin 費率已公告：${freeRateValue} NTD/coin`
      );
    },
    onError: (err: Error) => alert(`公告失敗：${err.message}`),
  });

  const announceFeeMutation = useMutation({
    mutationFn: () =>
      setPlatformFeeRate(
        feePlatform,
        feePeriod,
        parseFloat(feeValue) || 0,
        feeNote
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-revenue-config"] });
      alert(
        `✅ ${feePlatform === "ios" ? "Apple" : "Google"} ${feePeriod} 抽成已設為 ${(parseFloat(feeValue) * 100).toFixed(1)}%`
      );
    },
    onError: (err: Error) => alert(`設定失敗：${err.message}`),
  });

  const isLoading = loadingDramas || loadingCompanies || loadingSys;

  const monthlyRates = sysConfig?.monthly_free_coin_rates ?? {};
  const announcedPeriods = Object.keys(monthlyRates).sort().reverse();

  const iosFeeEntries = sysConfig?.platform_fees?.ios ?? {};
  const androidFeeEntries = sysConfig?.platform_fees?.android ?? {};
  const iosFeePeriods = Object.keys(iosFeeEntries).sort().reverse();
  const androidFeePeriods = Object.keys(androidFeeEntries).sort().reverse();

  return (
    <AdminPage>
      <AdminPageHeader
        title="分潤設定"
        subtitle="每劇獨立設定 Paid coin 費率 · 每月 Free coin 費率公告"
      />

      {isLoading && <AdminLoadingSkeleton variant="mixed" />}

      {!isLoading && (
        <div className="space-y-6">
          {/* === Drama-level rate table === */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold">
                每劇 Paid coin 分潤費率
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                每部劇獨立設定每個付費金幣折算多少 NTD 分給片商。未設定的劇集分潤為 0，可點「設定費率」。
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="搜尋劇集標題 / ID / 片商…"
                    className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm bg-background"
                  />
                </div>
                <select
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm bg-background"
                >
                  <option value="">全部片商</option>
                  {companyOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={onlyUnset}
                    onChange={(e) => setOnlyUnset(e.target.checked)}
                    className="h-4 w-4"
                  />
                  只看未設定
                </label>
              </div>

              <div className="text-xs text-muted-foreground">
                共 {rows.length} 部劇集 · 已設定{" "}
                <span className="font-medium text-foreground">{setCount}</span> 部 · 未設定{" "}
                <span className="font-medium text-foreground">{unsetCount}</span> 部
                {(searchText || companyFilter || onlyUnset) && (
                  <> · 當前篩選結果 {filteredRows.length} 部</>
                )}
              </div>

              <AdminDataTable
                columns={tableColumns}
                data={filteredRows}
                emptyTitle="沒有符合條件的劇集"
                pageSize={25}
                defaultSort={{ key: "drama_title", dir: "asc" }}
                getRowId={(r: DramaRateRow) => r.drama_doc_id}
                renderCell={(row: DramaRateRow, col) => {
                  switch (col.key) {
                    case "drama_title":
                      return (
                        <div>
                          <div className="font-medium">{row.drama_title}</div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {row.drama_id}
                          </div>
                        </div>
                      );
                    case "company_name":
                      return (
                        <span className="text-sm">{row.company_name}</span>
                      );
                    case "rate":
                      return row.rate != null ? (
                        <span className="font-medium tabular-nums">
                          {row.rate.toFixed(3)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      );
                    case "actions":
                      return (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={row.rate != null ? "ghost" : "outline"}
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditForm({
                                open: true,
                                drama: row,
                                value:
                                  row.rate != null ? String(row.rate) : "",
                              });
                            }}
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1" />
                            {row.rate != null ? "編輯" : "設定費率"}
                          </Button>
                          {row.rate != null && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              title="清除費率設定"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  confirm(
                                    `確定要清除「${row.drama_title}」的費率設定？清除後本劇 payout = 0。`
                                  )
                                ) {
                                  clearRateMutation.mutate(row.drama_doc_id);
                                }
                              }}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      );
                    default:
                      return null;
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* === Monthly Free Rate Announcement (retained) === */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Megaphone className="h-4 w-4" />
                每月 Free coin 費率公告
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-4">
                依當月行銷預算公告 free coin 分潤費率。預設為 0（不分潤）。公告後即適用於當月所有解鎖。
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    月份
                  </label>
                  <input
                    type="month"
                    value={freeRatePeriod}
                    onChange={(e) => setFreeRatePeriod(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-background"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    費率 (NTD/coin)
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={freeRateValue}
                    onChange={(e) => setFreeRateValue(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-background"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    備註（可選）
                  </label>
                  <input
                    type="text"
                    value={freeRateNote}
                    onChange={(e) => setFreeRateNote(e.target.value)}
                    placeholder="例：5 月行銷活動補貼"
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-background"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={() => announceFreeRateMutation.mutate()}
                  disabled={announceFreeRateMutation.isPending}
                >
                  {announceFreeRateMutation.isPending ? "公告中..." : "公告費率"}
                </Button>
              </div>

              {announcedPeriods.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-muted-foreground mb-2">歷史公告紀錄</p>
                  <div className="space-y-1">
                    {announcedPeriods.slice(0, 6).map((p) => {
                      const entry = monthlyRates[p];
                      return (
                        <div key={p} className="flex items-center gap-3 text-sm">
                          <span className="font-medium w-20">{p}</span>
                          <span className="text-amber-700 dark:text-amber-400 font-medium">
                            {entry.rate.toFixed(3)} NTD/coin
                          </span>
                          {entry.note && (
                            <span className="text-xs text-muted-foreground truncate">
                              {entry.note}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* === Platform fee (Apple / Google 抽成) === */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                Apple / Google 抽成費率
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-4">
                按月份公告 iOS / Android 的 IAP 抽成比例。某月未公告 → 沿用最近早於該月的公告值；從無公告則視為 0%。
                目前業界常見區間：15%（小型開發者）~ 30%。
              </p>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">平台</label>
                  <select
                    value={feePlatform}
                    onChange={(e) => setFeePlatform(e.target.value as IapPlatform)}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-background"
                  >
                    <option value="ios">iOS (Apple)</option>
                    <option value="android">Android (Google)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">生效月份</label>
                  <input
                    type="month"
                    value={feePeriod}
                    onChange={(e) => setFeePeriod(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-background"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">抽成比例 (0–1)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={feeValue}
                    onChange={(e) => setFeeValue(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-background"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {feeValue && !isNaN(parseFloat(feeValue))
                      ? `= ${(parseFloat(feeValue) * 100).toFixed(1)}%`
                      : ""}
                  </p>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">備註（可選）</label>
                  <input
                    type="text"
                    value={feeNote}
                    onChange={(e) => setFeeNote(e.target.value)}
                    placeholder="例：Apple 小型開發者計畫"
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-background"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={() => announceFeeMutation.mutate()}
                  disabled={announceFeeMutation.isPending}
                >
                  {announceFeeMutation.isPending ? "設定中..." : "設定抽成"}
                </Button>
              </div>

              {(iosFeePeriods.length > 0 || androidFeePeriods.length > 0) && (
                <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">
                      iOS (Apple) 歷史
                    </p>
                    {iosFeePeriods.length === 0 ? (
                      <p className="text-xs text-muted-foreground">尚未設定</p>
                    ) : (
                      <div className="space-y-1">
                        {iosFeePeriods.slice(0, 6).map((p) => {
                          const entry = iosFeeEntries[p];
                          return (
                            <div key={p} className="flex items-center gap-3 text-sm">
                              <span className="font-medium w-20">{p}</span>
                              <span className="text-sky-700 dark:text-sky-400 font-medium">
                                {(entry.rate * 100).toFixed(1)}%
                              </span>
                              {entry.note && (
                                <span className="text-xs text-muted-foreground truncate">
                                  {entry.note}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Android (Google) 歷史
                    </p>
                    {androidFeePeriods.length === 0 ? (
                      <p className="text-xs text-muted-foreground">尚未設定</p>
                    ) : (
                      <div className="space-y-1">
                        {androidFeePeriods.slice(0, 6).map((p) => {
                          const entry = androidFeeEntries[p];
                          return (
                            <div key={p} className="flex items-center gap-3 text-sm">
                              <span className="font-medium w-20">{p}</span>
                              <span className="text-green-700 dark:text-green-400 font-medium">
                                {(entry.rate * 100).toFixed(1)}%
                              </span>
                              {entry.note && (
                                <span className="text-xs text-muted-foreground truncate">
                                  {entry.note}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* === Edit Dialog === */}
      {editForm.open && editForm.drama && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-[480px] shadow-xl">
            <CardHeader>
              <CardTitle>
                {editForm.drama.rate != null ? "編輯費率" : "設定費率"}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {editForm.drama.drama_title}
                <span className="font-mono ml-1">
                  （{editForm.drama.drama_id}）
                </span>
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">
                    Paid coin 費率 (NTD/coin)
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    autoFocus
                    value={editForm.value}
                    onChange={(e) =>
                      setEditForm({ ...editForm, value: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && editForm.value !== "") {
                        saveRateMutation.mutate();
                      }
                    }}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-background"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  每個 Paid coin 折算多少 NTD 分給片商。此費率僅適用本劇，未設定則本劇分潤為 0。
                </p>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() =>
                    setEditForm({ open: false, drama: null, value: "" })
                  }
                >
                  取消
                </Button>
                <Button
                  onClick={() => saveRateMutation.mutate()}
                  disabled={
                    saveRateMutation.isPending || editForm.value === ""
                  }
                >
                  {saveRateMutation.isPending ? "儲存中..." : "儲存"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminPage>
  );
}
